import {
  CustomDataGrid,
  MAutocomplete,
  MButton,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { Tab, Tabs } from "@mui/material";
import { useInstallation, useUser } from "@services/servicesGenericsTables";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { setsConfigLidActivity } from "../utils/constants";
import { useReport } from "../utils/useReport";
import { LidForm, Lid, IFormNewReport } from "@app/types/report.types";
import useAuth from "@app/context/AuthContext";
import dayjs from "dayjs";

interface ReportResultFormProps {
  mode: MODES_OPTIONS;
}

export interface ResponseService {
  date_end: string;
  sample_reference: string;
  client_reference: string | null;
  sample_type: string;
  analysis_type: string;
  isotope: string;
  lid_client: string;
  lid: string;
  activity?: string | number;
  activity_client?: string | number;
  uncertainty?: string | number;
  lid_csn_type?: "LID" | "ACTIVIDAD";
}

const FormLid = ({ mode }: ReportResultFormProps) => {
  const form = useFormContext();
  const isView = mode === MODES_OPTIONS.VIEW;
  const [tabValue, setTabValue] = useState<number>(0);
  const { fetchReportLid, loadingLid } = useReport();

  const { installationList } = useInstallation();
  const { userList } = useUser();
  const [actividadArray, setActividadArray] = useState<Lid[]>([]);
  const [lidArray, setLidArray] = useState<Lid[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    if (isView) handleOnclick();
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const currentValues = form.getValues();
    setTabValue(newValue);
    form.reset(currentValues, { keepDirty: true, keepTouched: true });
  };

  const handleOnclick = async () => {
    const data = await fetchReportLid(form.getValues() as LidForm);

    if (!data) {
      return;
    }

    const dataArray = Object.entries(data).map(([key, value]) => ({
      id: key,
      ...value,
    }));

    // Filtrar por tipo ACTIVIDAD
    let actividadArrayRaw = dataArray.filter(
      (item) => item.lid_csn_type?.includes("ACTIVIDAD")
    );

    // Agrupar todos los análisis por sample_reference
    const sampleGroups = new Map<string, Lid[]>();

    actividadArrayRaw.forEach((item) => {
      const key = item.sample_reference;
      if (!sampleGroups.has(key)) {
        sampleGroups.set(key, []);
      }
      sampleGroups.get(key)!.push(item);
    });

    const filteredActividad: Lid[] = [];
    
    sampleGroups.forEach((items) => {
      // Variable global para acumular las actividades de los que cumplen "solo lid"
      let globalSum = 0;
      
      items.forEach((item) => {
        const activity = parseFloat(String(item.activity || 0));
        const activityClient = parseFloat(String(item.activity_client || 0));
        if( activityClient > 0 && activity > 0 ){
          const lid = parseFloat(String(item.lid || 0));
          const uncertainty = parseFloat(String(item.uncertainty || 0));
          
          // Verificar si cumple la condición "solo lid": Actividad ≥ LID y/o Actividad ≥ Incertidumbre
          const cumpleSoloLid = activity >= lid || activity >= uncertainty;
          
          if (cumpleSoloLid) {
            globalSum += activity / activityClient;
          }
        }
      });
      
      // Si la suma global es >= 1, incluir TODOS los resultados de esa muestra
      if (globalSum >= 1) {
        items.forEach(item => { item.value = globalSum; });
        filteredActividad.push(...items);
      }
    });

    const actividadArray = filteredActividad;

    const lidArray = dataArray.filter(
      (item) => item.lid_csn_type?.includes("LID")
    );

    setActividadArray(actividadArray);
    setLidArray(lidArray);
  };

  useEffect(() => {
    const today = dayjs();
    if(form.getValues('evo_start_date_lid') == undefined) form.setValue("evo_start_date_lid", today);
    if(form.getValues('evo_end_date_lid') == undefined) form.setValue("evo_end_date_lid", today);
    if(form.getValues('created_by') == undefined) form.setValue("created_by", user);
  }, []);

  const handleChildSubmit = () => {
    if( form.getValues("installation_lid") == undefined ){
      form.setError('installation_lid', {
        type: "manual",
        message: "La instalación es requerida",
      });
      return;
    }

    handleOnclick();
  };

  return (
    <>
      <ResponsiveFormContainer columns={5}>
        <MAutocomplete
          key={JSON.stringify(form.getValues("installation_lid"))}
          name="installation_lid"
          options={installationList}
          label="Instalación"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          validation={{ required: "La instalación es requerida" }}
          disabled={isView}
        />
        <MDatePicker
          name="evo_start_date_lid"
          pickerType="date"
          label="Fecha inicial"
          validation={{
            required: "La fecha inicial es requerida",
          }}
          disabled={isView}
          maxDate={dayjs(form.watch("evo_end_date_lid"))}
        />
        <MDatePicker
          name="evo_end_date_lid"
          pickerType="date"
          label="Fecha final"
          validation={{
            required: "La fecha final es requerida",
          }}
          disabled={isView}
          minDate={dayjs(form.watch("evo_start_date_lid"))}
        />
        <MAutocomplete
          name="created_by"
          options={userList}
          label="Realizado por"
          getOptionLabel={(option) => option.username || ""}
          validation={{ required: "El analista es requerido" }}
          disabled={isView}
        />
        {!isView && (
          <MButton
            variant="contained"
            color="primary"
            text="Buscar"
            onClick={handleChildSubmit}
            sx={{ mt: 1 }}
            loading={loadingLid}
          />
        )}
      </ResponsiveFormContainer>
      <Tabs
        value={tabValue}
        onChange={handleChange}
        aria-label="Fields uncertainty tabs"
      >
        {setsConfigLidActivity.map((set, index) => (
          <Tab key={index} label={set.label} />
        ))}
      </Tabs>
      <CustomDataGrid
        rows={tabValue === 1 ? actividadArray : lidArray}
        columns={setsConfigLidActivity[tabValue].columns}
        search={false}
      />
      <ResponsiveFormContainer columns={5} mt={2}>
        <MAutocomplete
          key={JSON.stringify(form.getValues("approved_by"))}
          name="approved_by"
          options={userList}
          label="Aprobado por"
          getOptionLabel={(option) => option.username || ""}
          validation={{ required: "El analista es requerido" }}
          disabled={isView}
        />
      </ResponsiveFormContainer>
      <ResponsiveFormContainer columns={2} mt={2}>
        <MTextField
          name="comments"
          label="Comentarios"
          placeholder="Comentarios"
          type="textArea"
          rows={4}
          multiline
          fullWidth
          disabled={isView}
          step={0.01}
        />
      </ResponsiveFormContainer>
    </>
  );
};

export default FormLid;
